import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneHoursScheduleRule } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import {
  Text,
  CheckboxField,
  TimeRangeField,
  DropdownField,
  TextField,
  useControlledField,
  useFormField,
  Button,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  useModalControl,
  Modal,
  useTooltip,
  Heading,
} from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { BreakDuplicator, DuplicateHoursButton, HourDuplicator } from './duplicate';
import { EditBreakModal } from './edit-break-modal';
import { NewBreakModal } from './new-break-modal';
import { Break, DaysOfWeek, usePhoneHoursForm } from './use-phone-hours-form';

export type BreakRules = {
  [id: string]: PhoneHoursScheduleRule;
};

export type BreakSchedulesByDay = Record<
  DaysOfWeek,
  { startTime: string; endTime: string; breakName: string; id: string }[]
>;

interface PhoneHoursFormProps {
  nameForm: ReturnType<typeof usePhoneHoursForm>['formProps']['nameForm'];
  openSchedulesForm: ReturnType<typeof usePhoneHoursForm>['formProps']['openSchedulesForm'];
  breakSchedulesByDay: BreakSchedulesByDay;
  routingActionsEnabled?: boolean;
  availableBreakNames: string[];
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onBreakAdd: (day: DaysOfWeek) => void;
  onEditBreak: (id: string, newName: string) => void;
  onDeleteBreak: (id: string) => void;
  onDuplicateBreak: (props: { days: DaysOfWeek[]; id: string; sourceDay: DaysOfWeek }) => void;
  setHasAnyBreakConflict: (hasConflict: boolean) => void;
  isSmallMode?: boolean;
  showHeader?: boolean;
}

const TooltipWrapper: React.FC<{
  tooltipText: string;
  children: React.ReactNode;
  tooltipDisabled?: boolean;
}> = ({ tooltipText, children, tooltipDisabled = false }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return (
    <div {...triggerProps}>
      {children}
      {!tooltipDisabled && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
    </div>
  );
};

export const PhoneHoursForm: React.FC<PhoneHoursFormProps> = ({
  nameForm,
  openSchedulesForm,
  breakSchedulesByDay,
  routingActionsEnabled = false,
  availableBreakNames,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onBreakAdd,
  onEditBreak,
  onDuplicateBreak,
  setHasAnyBreakConflict,
  isSmallMode,
  showHeader = true,
}) => {
  const { t } = useTranslation('phone');
  const [hintObj, setHintObj] = usePreference('DuplicatePhoneHoursHint');
  const hasDisplayedHint = useRef(false);

  const labels = ['', t('Day'), t('Start Time'), t('End Time'), t('Break')];

  const scheduleValues = openSchedulesForm.values;
  const scheduleGetFieldProps = openSchedulesForm.getFieldProps;
  const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

  const getDayDisplay = (day: string, index: number) => {
    return isSmallMode ? shortDays[index] : day;
  };

  // Maintain stable break ordering - only reorder when modal reopens or breaks added/removed
  const [stableBreakOrder, setStableBreakOrder] = useState<BreakSchedulesByDay>(() => {
    // Initial ordering when modal opens
    const ordered: BreakSchedulesByDay = {} as BreakSchedulesByDay;
    fullDays.forEach((day) => {
      const breaksForDay = breakSchedulesByDay[day] || [];
      ordered[day] = [...breaksForDay].sort((a, b) => {
        if (!a.startTime && !b.startTime) return 0;
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return a.startTime.localeCompare(b.startTime);
      });
    });
    return ordered;
  });

  const prevBreakStructureRef = useRef<Record<DaysOfWeek, string[]>>({} as Record<DaysOfWeek, string[]>);

  useEffect(() => {
    try {
      const currentStructure: Record<DaysOfWeek, string[]> = {} as Record<DaysOfWeek, string[]>;
      let structureChanged = false;

      fullDays.forEach((day) => {
        const currentBreaks = breakSchedulesByDay[day] || [];
        currentStructure[day] = currentBreaks.map((b) => b.id).sort();

        const prevStructure = prevBreakStructureRef.current[day] || [];
        if (JSON.stringify(currentStructure[day]) !== JSON.stringify(prevStructure)) {
          structureChanged = true;
        }
      });

      if (structureChanged) {
        const newOrder: BreakSchedulesByDay = {} as BreakSchedulesByDay;
        fullDays.forEach((day) => {
          const breaksForDay = breakSchedulesByDay[day] || [];
          newOrder[day] = [...breaksForDay].sort((a, b) => {
            if (!a.startTime && !b.startTime) return 0;
            if (!a.startTime) return 1;
            if (!b.startTime) return -1;
            return a.startTime.localeCompare(b.startTime);
          });
        });

        setStableBreakOrder(newOrder);
        prevBreakStructureRef.current = currentStructure;
      } else {
        // Structure unchanged - maintain existing order but update data
        setStableBreakOrder((prevOrder) => {
          const updatedOrder: BreakSchedulesByDay = {} as BreakSchedulesByDay;
          fullDays.forEach((day) => {
            const currentBreaks = breakSchedulesByDay[day] || [];
            const stableBreaks = prevOrder[day] || [];
            const breakMap = new Map(currentBreaks.map((b) => [b.id, b]));

            updatedOrder[day] = stableBreaks
              .map((stableBreak) => breakMap.get(stableBreak.id))
              .filter((b): b is (typeof currentBreaks)[0] => Boolean(b));
          });
          return updatedOrder;
        });
      }
    } catch (error) {
      // Fallback to simple ordering if anything fails in production
      console.warn('Break ordering failed, falling back to simple sort:', error);
      setStableBreakOrder(breakSchedulesByDay);
    }
  }, [breakSchedulesByDay]);

  // Calculate if there are any conflicts across all days
  const hasAnyConflict = Object.values(stableBreakOrder).some((dayBreaks) =>
    dayBreaks.some((breakItem, index, allBreaks) =>
      allBreaks.some((otherBreak, otherIndex) => {
        if (index === otherIndex) return false; // Skip the current break
        const [startA, endA] = [breakItem.startTime, breakItem.endTime];
        const [startB, endB] = [otherBreak.startTime, otherBreak.endTime];
        return (
          (startA < endB && endA > startB) || // Overlapping intervals
          (startB < endA && endB > startA)
        );
      })
    )
  );

  const [duplicateSource, setDuplicateSource] = useState<{ day: DaysOfWeek | undefined; scope: string }>({
    day: undefined,
    scope: '',
  });

  const onToggleDuplicateHours = (props: { day: DaysOfWeek | undefined; scope: string }) => {
    setDuplicateSource((prev) =>
      prev.day === props.day && prev.scope === props.scope ? { day: undefined, scope: '' } : props
    );
  };

  /**
   * This is a delibrate on unmount effect.
   * The "stale" references here will work fine because we only need to run the function once
   */
  useEffect(() => {
    return () => {
      if (hintObj?.count && hintObj.count >= 3) return;
      setHintObj({ count: (hintObj?.count || 0) + 1 });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the global conflict state
  useEffect(() => {
    setHasAnyBreakConflict(hasAnyConflict);
  }, [hasAnyConflict, setHasAnyBreakConflict]);

  const onDuplicateHours = ({ days, sourceDay }: { days: DaysOfWeek[]; sourceDay: DaysOfWeek }) => {
    const sourceRange = openSchedulesForm.values[`range-${sourceDay}`];

    days
      .filter((d) => d !== sourceDay)
      .map((day) => {
        const openField = scheduleGetFieldProps(`selected-${day}`);
        const rangeField = scheduleGetFieldProps(`range-${day}`);

        openField.onChange({ name: `selected-${day}`, value: true });
        rangeField.onChange({ name: `range-${day}`, value: sourceRange });

        // Add and remove class to highlight duplicated days
        document.querySelector(`.day-container-${day}`)?.classList.add('duplicated');
        setTimeout(() => {
          document.querySelector(`.day-container-${day}`)?.classList.remove('duplicated');
        }, 1500);
      });
  };

  const nameFieldProps = nameForm.getFieldProps('name');

  return (
    <div>
      {showHeader && (
        <div css={{ display: 'flex', gap: theme.spacing(1), flexDirection: 'column', marginBottom: theme.spacing(2) }}>
          <Heading level={3}>{t('Edit Phone Hours')}</Heading>
          <Text
            size='medium'
            css={{
              borderBottom: `1px solid ${theme.colors.neutral20}`,
              paddingBottom: theme.spacing(2),
              marginBottom: theme.spacing(1),
            }}
          >
            {t('Define Open, Closed, and Break hours in order to route calls differently by time of day.')}
          </Text>
        </div>
      )}

      {/* Phone Hours Name Field */}
      <div css={{ marginBottom: theme.spacing(3), marginTop: theme.spacing(1) }}>
        <TextField
          {...nameFieldProps}
          label={t('Phone Hours Name')}
          error={t('Phone Hours must have a name.')}
          autoFocus={!nameFieldProps.value}
          css={{ maxWidth: theme.spacing(40) }}
        />
      </div>

      {/* Schedule Form Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isSmallMode
            ? `10px minmax(0px, 0.8fr) minmax(76px, 1fr) minmax(120px, 1fr) 40px`
            : `28px 216px 160px 180px 40px`,
          rowGap: isSmallMode ? theme.spacing(3) : theme.spacing(4),
          position: 'relative',
        }}
      >
        {/* Header Row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            alignItems: 'center',
            gridColumn: '1/-1',
            borderBottom: `1px solid ${theme.colors.neutral20}`,
            paddingBottom: theme.spacing(1),
          }}
        >
          {labels.map((label, index) => (
            <Text key={label} size='medium' color='subdued' css={{ gridColumn: index + 1 }}>
              {label}
            </Text>
          ))}
        </div>

        {/* Days of the Week Rows */}
        {fullDays.map((day, index) => {
          const disabled = duplicateSource.day && day !== duplicateSource.day;
          const displayDay = getDayDisplay(day, index);
          return (
            <DayContainer
              hasDisplayedHint={hasDisplayedHint}
              disabled={disabled}
              key={day}
              day={day}
              displayDay={displayDay}
              isOpen={!!scheduleValues[`selected-${day}`]}
              openCheckField={
                <CheckboxField
                  disabled={disabled}
                  {...scheduleGetFieldProps(`selected-${day}`)}
                  label=''
                  hideErrorText
                />
              }
              timeRangeField={
                <TimeRangeField
                  disabled={disabled}
                  {...scheduleGetFieldProps(`range-${day}`)}
                  label={day}
                  interval={15}
                  hideErrorText
                  joiningText='to'
                  css={
                    isSmallMode
                      ? css`
                          .start-time,
                          .end-time {
                            width: ${isSmallMode ? 'clamp(100px, 17vw, 130px)' : '130px'} !important;
                          }
                        `
                      : undefined
                  }
                />
              }
              availableBreakNames={availableBreakNames}
              routingActionsEnabled={routingActionsEnabled}
              toggleDuplicateHours={onToggleDuplicateHours}
              duplicateSource={duplicateSource}
              onBreakAdd={onBreakAdd}
              onBreakChange={onBreakChange}
              onBreakRemove={onBreakRemove}
              breakSchedulesByDay={stableBreakOrder}
              onBreakTimeChange={onBreakTimeChange}
              onEditBreak={onEditBreak}
              onDuplicateBreak={onDuplicateBreak}
              onDuplicateHours={onDuplicateHours}
              isSmallMode={isSmallMode}
            />
          );
        })}
      </div>
    </div>
  );
};

type DayContainerProps = {
  day: DaysOfWeek;
  displayDay: string;
  hasDisplayedHint: MutableRefObject<boolean>;
  disabled?: boolean;
  isOpen: boolean;
  openCheckField: ReactNode;
  timeRangeField: ReactNode;
  toggleDuplicateHours: (props: { day: DaysOfWeek | undefined; scope: string }) => void;
  duplicateSource: { day: DaysOfWeek | undefined; scope: string };
  routingActionsEnabled: boolean;
  availableBreakNames: string[];
  onBreakAdd: (day: DaysOfWeek) => void;
  breakSchedulesByDay: BreakSchedulesByDay;
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onEditBreak: (id: string, newName: string) => void;
  onDuplicateBreak: (props: { days: DaysOfWeek[]; id: string; sourceDay: DaysOfWeek }) => void;
  onDuplicateHours: (props: { days: DaysOfWeek[]; sourceDay: DaysOfWeek }) => void;
  isSmallMode?: boolean;
};

const DayContainer = ({
  day,
  displayDay,
  disabled,
  isOpen,
  openCheckField,
  timeRangeField,
  hasDisplayedHint,
  routingActionsEnabled,
  availableBreakNames,
  toggleDuplicateHours,
  duplicateSource,
  onBreakAdd,
  breakSchedulesByDay,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onEditBreak,
  onDuplicateBreak,
  onDuplicateHours,
  isSmallMode,
}: DayContainerProps) => {
  const { t } = useTranslation('phone');
  const { Tooltip, tooltipProps, triggerProps, setOpen } = useTooltip({
    trigger: 'hover',
  });
  const [hintObj] = usePreference('DuplicatePhoneHoursHint');
  const shouldDisplayHint = !hintObj || (hintObj?.count && hintObj.count <= 2);

  const onCancelHourDuplicator = () => {
    toggleDuplicateHours({ day: undefined, scope: '' });
  };

  const onToggleDuplicateHours = () => {
    toggleDuplicateHours({ day, scope: '' });
  };

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        alignItems: 'center',
        gridColumn: '1/-1',
        rowGap: theme.spacing(1),
        transition: 'all 0.8s ease-in-out',
        outline: `12px solid transparent`,
        borderRadius: theme.borderRadius.small,

        '&.duplicated': {
          background: theme.colors.indigo10,
          outlineColor: theme.colors.indigo10,
        },
      }}
      className={`day-container-${day}`}
    >
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          alignItems: 'center',
          gridColumn: '1/-1',
        }}
      >
        {/* Checkbox */}
        <div>{openCheckField}</div>

        {/* Icon and Label */}
        {!isSmallMode && (
          <div
            style={{
              display: 'flex',
              gap: theme.spacing(1),
              alignItems: 'center',
            }}
          >
            <TooltipWrapper tooltipText={isOpen ? t('Open') : t('Closed')}>
              <div
                style={{
                  backgroundColor: isOpen ? theme.colors.warning5 : theme.colors.neutral10,
                  width: theme.spacing(4),
                  height: theme.spacing(3),
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '4px',
                }}
              >
                {isOpen ? <Icon name='sun-small' /> : <Icon name='moon-small' />}
              </div>
            </TooltipWrapper>
            <Text>{displayDay}</Text>
          </div>
        )}
        {isSmallMode && <Text css={{ marginLeft: theme.spacing(2) }}>{displayDay}</Text>}

        {/* TimeRangeField */}
        {isOpen ? (
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) max-content',
              alignItems: 'center',
              columnGap: theme.spacing(1),
              gridColumn: '3 / span 2',
            }}
          >
            <div
              onClick={() => {
                if (shouldDisplayHint && !hasDisplayedHint.current) {
                  setTimeout(() => {
                    setOpen(true);
                    hasDisplayedHint.current = true;
                  }, 1000);
                }
              }}
            >
              {timeRangeField}
            </div>
            <div {...triggerProps}>
              <DuplicateHoursButton
                disabled={disabled}
                onClick={() => {
                  onToggleDuplicateHours();
                }}
              />
            </div>
            {!disabled && <Tooltip {...tooltipProps}>{t('Duplicate hours')}</Tooltip>}
          </div>
        ) : (
          <Text
            css={{
              color: theme.colors.neutral20,
              backgroundColor: theme.colors.neutral5,
              padding: theme.spacing(1),
              margin: 0,
              width: '100%',
              borderRadius: theme.spacing(0.5),
              gridColumn: '3 / span 2',
            }}
          >
            {t('Closed')}
          </Text>
        )}

        {/* Add Break Icon */}
        <TooltipWrapper
          tooltipDisabled={!!Object.values(availableBreakNames).length || routingActionsEnabled || disabled}
          tooltipText={t('No existing breaks available to add. New breaks are created in the Call Route experience.')}
        >
          <Button
            name='add-break'
            iconName='add-small'
            variant='secondary'
            disabled={(!Object.values(availableBreakNames).length && !routingActionsEnabled) || disabled}
            css={css`
              width: 40px;
            `}
            onClick={() => {
              onBreakAdd(day);
            }}
          />
        </TooltipWrapper>
      </div>

      {duplicateSource.day === day && duplicateSource.scope === '' && (
        <div
          css={{
            gridColumn: '1/-1',
          }}
        >
          <HourDuplicator
            title={t('Duplicate hours to')}
            primary={{
              label: t('Apply'),
              onClick: (selectedDays) => {
                onDuplicateHours({ days: selectedDays, sourceDay: day });
                toggleDuplicateHours({ day: undefined, scope: '' });
              },
            }}
            secondary={{
              label: t('Cancel'),
              onClick: onCancelHourDuplicator,
            }}
            selectedDays={[day]}
            disabledDays={[day]}
          />
        </div>
      )}

      {/* Break Rows */}
      {Object.values(breakSchedulesByDay[day] ?? {}).length > 0 && (
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: '1/-1',
            backgroundColor: theme.colors.neutral5,
            padding: theme.spacing(1, 0),
            rowGap: theme.spacing(1),
          }}
        >
          {breakSchedulesByDay[day].map((breakItem, index, allBreaks) => {
            return (
              <BreakRowContainer
                disabled={disabled}
                key={breakItem.id}
                breakItem={breakItem}
                allBreaks={allBreaks}
                index={index}
                day={day}
                availableBreakNames={availableBreakNames}
                breakSchedulesByDay={breakSchedulesByDay}
                routingActionsEnabled={routingActionsEnabled}
                onBreakTimeChange={onBreakTimeChange}
                onBreakChange={onBreakChange}
                onBreakRemove={onBreakRemove}
                onEditBreak={onEditBreak}
                onDuplicateBreak={onDuplicateBreak}
                toggleDuplicateHours={toggleDuplicateHours}
                duplicateSource={duplicateSource}
                isSmallMode={isSmallMode}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

type BreakRowContainerProps = {
  disabled?: boolean;
  breakItem: Break;
  allBreaks: Break[];
  index: number;
  day: DaysOfWeek;
  availableBreakNames: string[];
  breakSchedulesByDay: BreakSchedulesByDay;
  routingActionsEnabled: boolean;
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onEditBreak: (id: string, newName: string) => void;
  onDuplicateBreak: (props: { days: DaysOfWeek[]; id: string; sourceDay: DaysOfWeek; ruleIndex: number }) => void;
  toggleDuplicateHours: (props: { day: DaysOfWeek | undefined; scope: string }) => void;
  duplicateSource: { day: DaysOfWeek | undefined; scope: string };
  isSmallMode?: boolean;
};

const BreakRowContainer = ({
  disabled,
  breakItem,
  allBreaks,
  index,
  day,
  availableBreakNames,
  breakSchedulesByDay,
  routingActionsEnabled,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onEditBreak,
  onDuplicateBreak,
  toggleDuplicateHours,
  duplicateSource,
  isSmallMode,
}: BreakRowContainerProps) => {
  const { t } = useTranslation('phone');

  const onCancelBreakDuplicator = () => {
    toggleDuplicateHours({ day: undefined, scope: '' });
  };

  const onToggleDuplicate = () => {
    toggleDuplicateHours({ day, scope: breakItem.id });
  };

  // Check for conflicts with other breaks on the same day
  const hasConflict = allBreaks.some((otherBreak, otherIndex) => {
    if (index === otherIndex) return false; // Skip the current break
    const [startA, endA] = [breakItem.startTime, breakItem.endTime];
    const [startB, endB] = [otherBreak.startTime, otherBreak.endTime];
    return (
      (startA < endB && endA > startB) || // Overlapping intervals
      (startB < endA && endB > startA)
    );
  });

  return (
    <>
      <div
        key={`${day}-${index}`}
        css={css`
          display: grid;

          grid-template-columns: subgrid;
          grid-column: 1/-1;
          align-items: center;
          padding: 10px 0;
          row-gap: ${theme.spacing(1)};
        `}
      >
        <BreakRow
          key={breakItem.id}
          disabled={disabled}
          hasConflict={hasConflict}
          name={breakItem.breakName}
          timeRange={[breakItem.startTime, breakItem.endTime]}
          // Filter out the break names that are already used in the same day (unless it's the current one since
          // we still want to show it as the selected option in the dropdown)
          breakNameOptions={availableBreakNames.filter(
            (name) =>
              name === breakItem.breakName || !(breakSchedulesByDay[day] ?? []).some((item) => item.breakName === name)
          )}
          existingBreakNames={availableBreakNames}
          routingActionsEnabled={routingActionsEnabled}
          day={day}
          id={breakItem.id}
          onBreakTimeChange={onBreakTimeChange}
          onBreakChange={onBreakChange}
          onBreakRemove={onBreakRemove}
          onEditBreak={onEditBreak}
          onClickDuplicate={onToggleDuplicate}
          isSmallMode={isSmallMode}
        />
      </div>

      {duplicateSource.day === day && duplicateSource.scope === breakItem.id && (
        <div
          css={{
            gridColumn: '1/-1',
            padding: theme.spacing(0, 2),
          }}
        >
          <BreakDuplicator
            title={t('Duplicate break to')}
            primary={{
              label: t('Apply'),
              onClick: (selectedDays) => {
                onDuplicateBreak({
                  // Filter out the current day from the selected days so we don't duplicate the break on the same day
                  days: selectedDays.filter((d) => d !== day),
                  id: breakItem.id,
                  sourceDay: day,
                  ruleIndex: index,
                });
                toggleDuplicateHours({ day: undefined, scope: '' });
              },
            }}
            secondary={{ label: t('Cancel'), onClick: onCancelBreakDuplicator }}
            breakSchedulesByDay={breakSchedulesByDay}
            day={day}
          />
        </div>
      )}
    </>
  );
};

const BreakRow = ({
  disabled,
  name,
  breakNameOptions,
  existingBreakNames,
  timeRange,
  day,
  id,
  routingActionsEnabled,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onEditBreak,
  onClickDuplicate,
  hasConflict,
  isSmallMode,
}: {
  disabled?: boolean;
  name: string;
  timeRange: [string, string];
  breakNameOptions: string[];
  existingBreakNames: string[];
  day: DaysOfWeek;
  id: string;
  routingActionsEnabled: boolean;
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onEditBreak: (breakId: string, newName: string) => void;
  onClickDuplicate: () => void;
  hasConflict: boolean;
  isSmallMode?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const {
    modalProps: editBreakModalProps,
    closeModal: editBreakCloseModal,
    openModal: editBreakOpenModal,
  } = useModalControl();
  const {
    modalProps: newBreakModalProps,
    closeModal: newBreakCloseModal,
    openModal: newBreakOpenModal,
  } = useModalControl();

  const nameField = useControlledField({
    type: 'dropdown',
    required: true,
    onChange: (value: string) => {
      if (value === '') {
        return;
      }
      onBreakChange({ day, breakName: value, id });
    },
    value: name,
  });

  // Uncontrolled (like DayContainer's time range fields): value + displayValue are owned together
  const timeRangeField = useFormField({ type: 'timeRange', required: true, interval: 60, value: timeRange }, [
    timeRange[0],
    timeRange[1],
  ]);

  const timeFieldCompleted = timeRangeField.displayValue.every((time) => time.endsWith('am') || time.endsWith('pm'));
  const showConflictError = hasConflict && timeFieldCompleted;

  // Propagate local edits up once they diverge from the last known external value.
  useEffect(() => {
    const [start, end] = timeRangeField.value;
    if (start !== timeRange[0] || end !== timeRange[1]) {
      onBreakTimeChange({ day, startTime: start, endTime: end, id });
    }
  }, [timeRangeField.value, timeRange, day, id, onBreakTimeChange]);

  const { getMenuProps, getTriggerProps, close: closePopupMenu } = usePopoverMenu();

  // Auto-select the break if there is only one available
  useEffect(() => {
    if (breakNameOptions.length === 1 && breakNameOptions[0] !== name) {
      onBreakChange({ day, breakName: breakNameOptions[0], id });
    }
  }, [breakNameOptions, name, day, id, onBreakChange]);

  return (
    <>
      <div />

      <DropdownField
        hideErrorText
        {...nameField}
        disabled={disabled}
        name='breakName'
        label={isSmallMode ? t('Break') : t('Select Break')}
        css={css`
          width: ${isSmallMode ? '88px' : '200px'};
          background-color: ${theme.colors.white};
        `}
      >
        <DropdownField.Option
          css={css`
            padding: 0;
          `}
          key={'new'}
          disabled={!routingActionsEnabled}
          value={''}
          isSelectable={false}
        >
          <TooltipWrapper
            tooltipDisabled={routingActionsEnabled}
            tooltipText={t('New breaks are created in the Call Route experience.')}
          >
            <Button
              iconName='plus'
              variant='tertiary'
              disabled={!routingActionsEnabled}
              onClick={() => newBreakOpenModal()}
              css={css`
                width: 100%;
                justify-content: start;
              `}
            >
              {t('New Break')}
            </Button>
          </TooltipWrapper>
        </DropdownField.Option>
        {breakNameOptions.map((breakName) => (
          <DropdownField.Option key={breakName} value={breakName}>
            {breakName}
          </DropdownField.Option>
        ))}
      </DropdownField>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) max-content',
          gridTemplateRows: showConflictError ? 'auto auto' : 'auto',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          gridColumn: 'span 2',
        }}
      >
        <TimeRangeField
          disabled={disabled}
          {...timeRangeField}
          name='breakTimeRange'
          label='Break Time Range'
          interval={15}
          hasError={hasConflict}
          hideErrorText={true}
          joiningText='to'
          css={css`
            .start-time,
            .end-time {
              width: ${isSmallMode ? 'clamp(100px, 17vw, 130px)' : '130px'} !important;
            }

            input {
              background-color: ${theme.colors.white} !important;
              border-radius: ${theme.spacing(0.5)};
            }
          `}
        />
      </div>

      {!name ? (
        <Button
          iconName='remove-small'
          variant='secondary'
          destructive
          css={css`
            width: 40px;
          `}
          onClick={() => {
            onBreakRemove({ day, id });
          }}
        />
      ) : (
        <>
          <Button
            iconName='more-small'
            variant='secondary'
            {...getTriggerProps()}
            css={css`
              width: 40px;
            `}
            disabled={disabled}
          />

          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem
              css={css`
                cursor: pointer;
              `}
              Icon={() => <Icon name='edit-small' />}
              onClick={() => {
                editBreakOpenModal();
                closePopupMenu();
              }}
            >
              <Text as='span'> {t('Rename Break')}</Text>
            </PopoverMenuItem>

            <PopoverMenuItem
              css={css`
                cursor: pointer;
              `}
              Icon={() => <Icon name='edit-small' />}
              onClick={() => {
                onClickDuplicate();
                closePopupMenu();
              }}
              trackingId={trackingId({ context: 'setting', feature: 'phone-hours', details: 'open-duplicate-hours' })}
            >
              <Text as='span'> {t('Duplicate Break')}</Text>
            </PopoverMenuItem>
            <PopoverMenuItem
              css={css`
                cursor: pointer;
              `}
              Icon={() => <Icon name='remove-small' />}
              onClick={() => {
                onBreakRemove({ day, id });
                closePopupMenu();
              }}
            >
              <Text as='span'> {t('Remove Break')}</Text>
            </PopoverMenuItem>
          </PopoverMenu>
        </>
      )}

      {/* Error message row */}
      {(!nameField.value || showConflictError) && (
        <div css={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1/-1' }}>
          {!nameField.value && (
            <Text
              color='error'
              size='small'
              css={css`
                grid-column: 2;
              `}
            >
              {t('This field is required.')}
            </Text>
          )}
          {showConflictError && (
            <Text
              color='error'
              size='small'
              css={css`
                grid-column: 3 / span 2;
              `}
            >
              {t('This break overlaps with another break.')}
            </Text>
          )}
        </div>
      )}

      <Modal {...editBreakModalProps} minWidth={400}>
        <EditBreakModal
          name={name}
          // Filter out the current name from the existing names to allow renaming to the same name
          existingBreakNames={existingBreakNames.filter((existingName) => existingName !== name)}
          onCancel={editBreakCloseModal}
          onDone={(newName) => {
            editBreakCloseModal();
            if (newName !== name) {
              onEditBreak(id, newName);
            }
          }}
        />
      </Modal>

      <Modal {...newBreakModalProps} minWidth={400}>
        <NewBreakModal
          onCancel={newBreakCloseModal}
          existingBreakNames={existingBreakNames}
          onDone={(newName) => {
            onBreakChange({ day, breakName: newName, id, isNew: true });
            // Keep dropdown selection in sync immediately so required validation does not flash/persist.
            nameField.onChange?.({ name: 'breakName', value: newName });
            newBreakCloseModal();
          }}
        />
      </Modal>
    </>
  );
};
